import { useCallback, useEffect, useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FaCalendarAlt, FaChalkboardTeacher, FaChartBar, FaChild, FaCog, FaHome, FaList, FaPlay, FaSave, FaTable } from "react-icons/fa";
import BackButton from "../../components/navigation/BackButton";
import Modal from "../../components/Modal";
import Spinner from "../../components/Spinner";
import { LdsOverviewHero, LdsOverviewPage } from "../../components/lds/LdsOverview";
import { useUnsavedChangesGuard } from "../../components/navigation/UnsavedChangesGuard";
import { createReport, fetchCatalog, fetchReport, runReport, updateReport } from "./api";
import ColumnBar from "./components/ColumnBar";
import ExportButtons from "./components/ExportButtons";
import FieldPanel from "./components/FieldPanel";
import FilterBar from "./components/FilterBar";
import LayoutPreview from "./components/LayoutPreview";
import ResultTable from "./components/ResultTable";
import SummaryEditor from "./components/SummaryEditor";
import SummaryResult from "./components/SummaryResult";
import { card, fitWidth, ghostButton, input, primaryButton, secondaryButton } from "./components/styles";
import {
  definitionProblems,
  moveColumn,
  newDefinition,
  renameColumn,
  runKey,
  sameDefinition,
  setMode,
  setSort,
  toggleColumn,
  withoutUnavailable,
} from "./definition";
import { reportErrorMessage } from "./errors";
import { labelOf, schoolYearLabel, sourceLabel, summarySentence } from "./labels";
import { useReportRun } from "./useReportRun";
import type { Catalog, ReportSpec } from "./types";

const PAGE_SIZE = 50;

/**
 * Compose a report. While composing only the catalog is used (objects, fields, relations); data is fetched
 * only when the user chooses "Run report".
 */
export default function ReportBuilderPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [definition, setDefinition] = useState<ReportSpec | null>(null);
  const [saved, setSaved] = useState<{ name: string; description: string; definition: ReportSpec | null }>({
    name: "", description: "", definition: null,
  });
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [dropped, setDropped] = useState<string[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showFields, setShowFields] = useState(false);
  const [askName, setAskName] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const reportRun = useReportRun(t);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const loaded = await fetchCatalog();
        if (cancelled) return;
        setCatalog(loaded);
        if (id) {
          const report = await fetchReport(id);
          if (cancelled) return;
          const cleaned = withoutUnavailable(loaded, report.definition);
          setDefinition(cleaned.definition);
          setDropped(cleaned.dropped);
          setName(report.name);
          setDescription(report.description ?? "");
          setSaved({ name: report.name, description: report.description ?? "", definition: report.definition });
        } else {
          const source = searchParams.get("source");
          if (source && loaded.sources.some((item) => item.key === source)) {
            setDefinition(newDefinition(loaded, source));
          }
        }
      } catch (caught) {
        if (!cancelled) setLoadError(reportErrorMessage(t, caught));
      }
    })();
    return () => { cancelled = true; };
    // The catalog and report are loaded once per page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const dirty = Boolean(definition) && (
    !saved.definition
    || !sameDefinition(saved.definition, definition as ReportSpec)
    || name.trim() !== saved.name
    || description.trim() !== saved.description
  );
  const { leaveWithoutWarning } = useUnsavedChangesGuard(dirty);

  const problems = useMemo(
    () => (catalog && definition ? definitionProblems(catalog, definition) : []),
    [catalog, definition],
  );
  const schoolYearId = definition?.context.schoolYear.mode === "FIXED"
    ? definition.context.schoolYear.id ?? null
    : catalog?.currentSchoolYearId ?? null;
  const currentKey = definition ? runKey(definition) : null;
  const stale = Boolean(reportRun.result) && reportRun.ranKey !== currentKey;

  const run = useCallback((page = 0) => {
    if (!definition || problems.length > 0) return;
    const snapshot = definition;
    reportRun.run((p, signal) => runReport(snapshot, p, PAGE_SIZE, signal), page, runKey(snapshot));
  }, [definition, problems.length, reportRun]);

  const update = (next: ReportSpec) => setDefinition(next);

  const persist = async (reportName: string) => {
    if (!definition || saving) return;
    setSaving(true);
    setSaveError(null);
    const request = { name: reportName.trim(), description: description.trim() || null, definition };
    try {
      if (id) {
        const result = await updateReport(id, request);
        setSaved({ name: result.name, description: result.description ?? "", definition });
        setName(result.name);
      } else {
        const result = await createReport(request);
        setSaved({ name: result.name, description: result.description ?? "", definition });
        leaveWithoutWarning(() => navigate(`/reports/builder/${result.id}/edit`, { replace: true }));
      }
      setAskName(false);
    } catch (caught) {
      setSaveError(reportErrorMessage(t, caught));
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <LdsOverviewPage>
        <p role="alert" className="text-sm text-rose-700 dark:text-rose-200">{loadError}</p>
        <BackButton fallback="/reports/builder" />
      </LdsOverviewPage>
    );
  }

  if (!catalog) {
    return <LdsOverviewPage><div className="flex justify-center py-10"><Spinner /></div></LdsOverviewPage>;
  }

  if (!definition) {
    return <SourcePicker catalog={catalog} onPick={(source) => setDefinition(newDefinition(catalog, source))} />;
  }

  const ownOnly = Object.keys(catalog.entities).some((key) => catalog.entities[key].restrictedToOwnClasses);
  const summaryMode = Boolean(definition.summary);
  const showsSummary = Boolean(reportRun.result?.columns.some((column) => column.display === "GROUP"));
  const problemText = problems.includes("NO_COLUMNS")
    ? t("reports.builder.problem_columns", { defaultValue: "Choose at least one column." })
    : problems.includes("NO_GROUPS")
      ? t("reports.builder.problem_groups", { defaultValue: "Choose what to group by." })
    : problems.includes("INCOMPLETE_CONDITION")
      ? t("reports.builder.problem_condition", { defaultValue: "Complete or remove the highlighted condition." })
      : problems.includes("UNAVAILABLE_PATH")
        ? t("reports.builder.problem_unavailable", { defaultValue: "Remove the parts that are not available." })
        : null;

  return (
    <LdsOverviewPage>
      <div><BackButton fallback="/reports/builder" /></div>
      <LdsOverviewHero
        icon={summaryMode ? FaChartBar : FaTable}
        eyebrow={String(t("reports.builder.title", { defaultValue: "Report builder" }))}
        title={name.trim() || String(id
          ? t("reports.builder.untitled", { defaultValue: "Untitled report" })
          : t("reports.builder.new", { defaultValue: "New report" }))}
        description={[
          summarySentence(t, catalog, definition),
          reportRun.result && !stale
            ? String(t(showsSummary ? "reports.builder.group_count" : "reports.builder.row_count", {
              count: reportRun.result.total,
              defaultValue: showsSummary ? "{{count}} groups" : "{{count}} rows",
            }))
            : null,
          ownOnly ? String(t("reports.builder.own_classes_only", { defaultValue: "Only from your own classes" })) : null,
        ].filter(Boolean).join(" · ")}
      />

      {/* Toolbar: name, table or summary, run, export and save. */}
      <section className={`${card} ${fitWidth} space-y-3 p-4 sm:p-5`}>
        <div className="flex flex-wrap items-center gap-3">
          <label className="min-w-[12rem] flex-1">
            <span className="sr-only">{t("reports.builder.name", { defaultValue: "Name" })}</span>
            <input
              value={name}
              maxLength={120}
              onChange={(event) => setName(event.target.value)}
              placeholder={String(t("reports.builder.untitled", { defaultValue: "Untitled report" }))}
              className={`${input} h-11 text-base font-bold`}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button type="button" className={secondaryButton} onClick={() => setShowAdvanced((open) => !open)}
                    aria-expanded={showAdvanced}>
              <FaCog aria-hidden /> {t("reports.builder.advanced", { defaultValue: "Advanced" })}
            </button>
            <button type="button" className={secondaryButton} disabled={problems.length > 0 || reportRun.loading}
                    onClick={() => run(0)}>
              <FaPlay aria-hidden /> {t("reports.builder.run", { defaultValue: "Run report" })}
            </button>
            <ExportButtons
              catalog={catalog}
              definition={definition}
              reportId={id && !dirty ? id : null}
              name={name.trim() || String(t("reports.builder.untitled", { defaultValue: "Untitled report" }))}
              subtitle={summarySentence(t, catalog, definition)}
              disabled={problems.length > 0}
              onError={setExportError}
            />
            <button
              type="button"
              className={primaryButton}
              disabled={problems.length > 0 || saving || (Boolean(id) && !dirty)}
              onClick={() => (id && name.trim() ? persist(name) : setAskName(true))}
            >
              <FaSave aria-hidden /> {t("reports.builder.save", { defaultValue: "Save" })}
            </button>
          </div>
        </div>
        <div role="tablist" aria-label={String(t("reports.builder.view", { defaultValue: "View" }))}
             className="inline-flex rounded-full border border-slate-200 p-1 dark:border-white/10">
          {([["TABLE", FaTable, t("reports.builder.view_table", { defaultValue: "Table" })],
            ["SUMMARY", FaChartBar, t("reports.builder.view_summary", { defaultValue: "Summary" })]] as const)
            .map(([mode, Icon, text]) => {
              const active = (mode === "SUMMARY") === summaryMode;
              return (
                <button key={mode} type="button" role="tab" aria-selected={active}
                        onClick={() => update(setMode(definition, mode))}
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-bold transition ${active
                          ? "bg-indigo-600 text-white dark:bg-indigo-500"
                          : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/10"}`}>
                  <Icon aria-hidden /> {String(text)}
                </button>
              );
            })}
        </div>
        {problemText && <p className="text-xs font-bold text-amber-700 dark:text-amber-300">{problemText}</p>}
        {saveError && <p role="alert" className="text-sm text-rose-700 dark:text-rose-200">{saveError}</p>}
        {exportError && <p role="alert" className="text-sm text-rose-700 dark:text-rose-200">{exportError}</p>}
        {dropped.length > 0 && (
          <p role="status" className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-100">
            {t("reports.builder.dropped", {
              count: dropped.length,
              defaultValue: "{{count}} part(s) of this report are not available to you and were removed. Save to keep it this way.",
            })}
          </p>
        )}
        {showAdvanced && (
          <div className="flex flex-wrap items-end gap-4 rounded-2xl border border-slate-200 p-3 dark:border-white/10">
            <label className="text-xs font-bold text-slate-600 dark:text-slate-300">
              {t("reports.builder.school_year", { defaultValue: "School year" })}
              <select
                className={`${input} mt-1 w-56`}
                value={definition.context.schoolYear.mode === "FIXED" ? definition.context.schoolYear.id ?? "" : "CURRENT"}
                onChange={(event) => update({
                  ...definition,
                  context: {
                    schoolYear: event.target.value === "CURRENT"
                      ? { mode: "CURRENT", id: null }
                      : { mode: "FIXED", id: event.target.value },
                  },
                })}
              >
                <option value="CURRENT">{t("reports.builder.school_year_current_option", { defaultValue: "Current school year" })}</option>
                {catalog.schoolYears.map((year) => (
                  <option key={year.id} value={year.id}>{schoolYearLabel(year)}</option>
                ))}
              </select>
            </label>
            <label className="min-w-[14rem] flex-1 text-xs font-bold text-slate-600 dark:text-slate-300">
              {t("reports.builder.report_description", { defaultValue: "Description (optional)" })}
              <input className={`${input} mt-1`} value={description} maxLength={500}
                     onChange={(event) => setDescription(event.target.value)} />
            </label>
          </div>
        )}
      </section>

      <div className={`grid ${fitWidth} gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]`}>
        <aside className="space-y-2">
          <button type="button" className={`${secondaryButton} w-full lg:hidden`} aria-expanded={showFields}
                  onClick={() => setShowFields((open) => !open)}>
            <FaList aria-hidden /> {summaryMode
              ? t("reports.builder.summary_settings", { defaultValue: "Summary" })
              : t("reports.builder.fields_title", { defaultValue: "Data" })}
          </button>
          <div className={`${showFields ? "block" : "hidden"} lg:block ${card} p-3`}>
            {summaryMode
              ? <SummaryEditor catalog={catalog} definition={definition} onChange={update} />
              : <FieldPanel catalog={catalog} definition={definition} onToggle={(path) => update(toggleColumn(definition, path))} />}
          </div>
        </aside>

        <div className="min-w-0 space-y-4">
          <section className={`${card} space-y-3 p-4`}>
            <FilterBar catalog={catalog} definition={definition} schoolYearId={schoolYearId}
                       showGroups={showAdvanced} onChange={update} />
            {!summaryMode && <ColumnBar
              catalog={catalog}
              definition={definition}
              onMove={(from, to) => update(moveColumn(definition, from, to))}
              onRemove={(path) => update(toggleColumn(definition, path))}
              onRename={(path, label) => update(renameColumn(definition, path, label))}
              onSort={(path, direction) => update(setSort(definition, path, direction))}
            />}
          </section>

          {(summaryMode ? (definition.summary?.groups.length ?? 0) === 0 : definition.columns.length === 0) ? (
            <section className={`${card} px-6 py-10 text-center text-sm text-slate-600 dark:text-slate-300`}>
              {summaryMode
                ? t("reports.builder.empty_groups", { defaultValue: "Choose on the left what to group by, for example class or registration year." })
                : t("reports.builder.empty_columns", { defaultValue: "Choose on the left which data you want to see." })}
            </section>
          ) : reportRun.result && showsSummary === summaryMode ? (showsSummary ? (
            <SummaryResult
              catalog={catalog}
              definition={definition}
              result={reportRun.result}
              stale={stale}
              loading={reportRun.loading}
              onPage={(page) => run(page)}
            />
          ) : (
            <ResultTable
              catalog={catalog}
              definition={definition}
              result={reportRun.result}
              stale={stale}
              loading={reportRun.loading}
              onPage={(page) => run(page)}
            />
          )) : (
            <>
              <LayoutPreview catalog={catalog} definition={definition} />
              <p className="text-center text-sm text-slate-500 dark:text-slate-400">
                {reportRun.loading
                  ? t("reports.builder.running", { defaultValue: "Running the report…" })
                  : t("reports.builder.run_hint", { defaultValue: "Ready? Choose Run report to see the data." })}
              </p>
            </>
          )}
          {reportRun.error && <p role="alert" className="text-sm text-rose-700 dark:text-rose-200">{reportRun.error}</p>}
        </div>
      </div>

      <Modal
        show={askName}
        title={t("reports.builder.save_title", { defaultValue: "Save report" })}
        onClose={() => setAskName(false)}
        footer={(
          <div className="flex justify-end gap-2">
            <button type="button" className={ghostButton} onClick={() => setAskName(false)}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </button>
            <button type="button" className={primaryButton} disabled={!name.trim() || saving} onClick={() => persist(name)}>
              <FaSave aria-hidden /> {t("reports.builder.save", { defaultValue: "Save" })}
            </button>
          </div>
        )}
      >
        <label className="block text-sm font-bold text-slate-700 dark:text-slate-200">
          {t("reports.builder.name", { defaultValue: "Name" })}
          <input
            autoFocus
            className={`${input} mt-1`}
            value={name}
            maxLength={120}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => { if (event.key === "Enter" && name.trim()) persist(name); }}
          />
        </label>
        {saveError && <p role="alert" className="mt-2 text-sm text-rose-700 dark:text-rose-200">{saveError}</p>}
      </Modal>
    </LdsOverviewPage>
  );
}

function SourcePicker({ catalog, onPick }: { catalog: Catalog; onPick: (source: string) => void }):
  ReactElement {
  const { t } = useTranslation();
  return (
    <LdsOverviewPage>
      <div><BackButton fallback="/reports/builder" /></div>
      <LdsOverviewHero
        icon={FaList}
        eyebrow={String(t("reports.builder.title", { defaultValue: "Report builder" }))}
        title={String(t("reports.builder.pick_title", { defaultValue: "What should each row be?" }))}
        description={String(t("reports.builder.pick_description", {
          defaultValue: "Choose where the report starts. You can add related data afterwards.",
        }))}
      />
      {catalog.sources.length === 0 ? (
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {t("reports.builder.no_sources", { defaultValue: "You do not have access to data a report can start from." })}
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {catalog.sources.map((source) => {
            const entity = catalog.entities[source.entity];
            const Icon = { child: FaChild, family: FaHome, classroom: FaChalkboardTeacher, schoolYear: FaCalendarAlt }[source.entity]
              ?? FaList;
            return (
              <button
                key={source.key}
                type="button"
                onClick={() => onPick(source.key)}
                className="group rounded-[24px] border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-lg dark:border-white/10 dark:bg-slate-900/80"
              >
                <span className="inline-flex items-center gap-2 text-lg font-bold text-slate-950 dark:text-white">
                  <Icon aria-hidden className="text-2xl text-indigo-600 dark:text-indigo-300" />
                  {sourceLabel(t, catalog, source.key)}
                </span>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                  {String(t(source.descriptionKey, { defaultValue: source.defaultDescription }))}
                </p>
                {entity && entity.relations.length > 0 && (
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    {t("reports.builder.pick_related", {
                      list: entity.relations.map((relation) => labelOf(t, relation)).join(", "),
                      defaultValue: "With: {{list}}",
                    })}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      )}
    </LdsOverviewPage>
  );
}
